import { useEffect, useState } from 'react';

// The frozen title column: on desktop a fixed measure; on a phone a share
// of the viewport so real value columns stay visible beside it.
export const DESKTOP_TITLE_WIDTH = 320;
const PHONE_TITLE_SHARE = 0.44;
const PHONE_TITLE_MIN = 140;

// The route's sticky header: the context bar, or the shell header that replaces
// it on the collection's own surfaces.
const HEADER_SELECTOR = '.shell-header, .context-bar';

/**
 * The sticky header sits beneath the route's sticky header: the context bar
 * (`.context-bar`, shell.css) or the shell's `.shell-header`. Its height depends
 * on its content and the breakpoint (the mobile shell's bar is shorter, and its actions may wrap).
 * Measure it rather than guess: the bar is sticky at top: 0, so its
 * rendered height IS the offset. Without a bar (a test, a surface outside
 * the shell) the header sticks to the top of the window.
 *
 * A bar that is not pinned (the shell header on a phone: `position: static`,
 * docs/platform-standards.md §13.2) scrolls away, so it offsets nothing: the
 * table's header then sticks to the top of the window.
 */
const barOffset = bar => {
	if (!bar) return 0;
	if (typeof window !== 'undefined' && window.getComputedStyle(bar).position === 'static') return 0;
	return Math.round(bar.getBoundingClientRect().height);
};

// The items page's two-row toolbar, pinned beneath that header from 768px up
// (KPR-1724): the column header sticks beneath it, so a pinned toolbar adds
// its height. Unpinned (a phone) or absent (a related list) it adds nothing.
const TOOLBAR_SELECTOR = '.items-page__toolbar--two-row';

const toolbarOffset = toolbar => {
	if (!toolbar) return 0;
	if (typeof window !== 'undefined' && window.getComputedStyle(toolbar).position !== 'sticky') return 0;
	return Math.round(toolbar.getBoundingClientRect().height);
};

const measureContextBar = () => {
	if (typeof document === 'undefined') return 0;
	return barOffset(document.querySelector(HEADER_SELECTOR))
		+ toolbarOffset(document.querySelector(TOOLBAR_SELECTOR));
};

export const useStickyHeaderOffset = () => {
	const [offset, setOffset] = useState(measureContextBar);
	useEffect(() => {
		if (typeof document === 'undefined') return undefined;
		const bar = document.querySelector(HEADER_SELECTOR);
		const toolbar = document.querySelector(TOOLBAR_SELECTOR);
		if (!bar && !toolbar) return undefined;
		const update = () => setOffset(barOffset(bar) + toolbarOffset(toolbar));
		update();
		// The pin comes and goes with the window's width without the bar's own
		// size changing, so a resize re-reads it too.
		window.addEventListener('resize', update);
		const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
		if (bar) observer?.observe(bar);
		// The toolbar grows when its chips wrap or a filter is applied.
		if (toolbar) observer?.observe(toolbar);
		return () => {
			window.removeEventListener('resize', update);
			observer?.disconnect();
		};
	}, []);
	return offset;
};

const readViewportWidth = () => (
	typeof window !== 'undefined' && Number.isFinite(window.innerWidth) ? window.innerWidth : 390
);

// The viewport width, live: a phone turning sideways re-sizes the frozen
// title column with it.
export const useViewportWidth = () => {
	const [width, setWidth] = useState(readViewportWidth);
	useEffect(() => {
		if (typeof window === 'undefined') return undefined;
		const onResize = () => setWidth(readViewportWidth());
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);
	return width;
};

export const phoneTitleWidth = viewport => Math.max(PHONE_TITLE_MIN, Math.round(viewport * PHONE_TITLE_SHARE));
